import React from 'react';
import { ReactComponent as ReplyIcon } from '../images/kickbadges/kick-reply-icon.svg';
import { ReactComponent as BannedIcon } from '../images/kickbadges/banned.svg';
import { ReactComponent as UnBannedIcon } from '../images/kickbadges/unbanned.svg';
import { ReactComponent as TimeoutIcon } from '../images/kickbadges/timeout.svg';
import { ReactComponent as TrashIcon } from '../images/kickbadges/kick-trash-icon.svg';
import { ReactComponent as CelebrateIcon } from '../images/kickbadges/kick-celebrate.svg';
import { renderBadges } from './badges';
import { renderMessageContent, renderTruncatedContent } from './KickContent';

const REPLY_PREVIEW_EMOTE = { className: 'message-emote reply-preview-emote', style: { height: '1.2em', verticalAlign: 'middle' } };

const EVENT_TYPES = new Set(['subscription', 'gifted_subs', 'gifted_subs_lucky', 'celebration']);

// Name and description for a sub/gift/celebration. kick-chat-service sends sub and gift text with
// the username first ("sub_sam subscribed for 3 month(s)!"), so the name is split off for its own line.
function describeEvent(m) {
  const username = m.sender?.username || '';
  if (m.type === 'celebration') {
    const celebData = m.metadata?.celebration;
    if (celebData?.type === 'subscription_renewed') {
      const months = celebData.total_months;
      return { username, description: `celebrates their subscription for ${months} month${months !== 1 ? 's' : ''}!` };
    }
    return { username, description: 'is celebrating!' };
  }
  const content = m.content || '';
  if (username && content.startsWith(`${username} `)) {
    const rest = content.slice(username.length + 1);
    // e.g. "Gifted 5 subscriptions..." reads as a sentence fragment under the name
    return { username, description: rest.charAt(0).toLowerCase() + rest.slice(1) };
  }
  return { username: '', description: content };
}

// React key for a feed entry, matching what each entry type has always used
export const kickFeedItemKey = (m, index) =>
  m.type === 'separator' ? `separator-${index}` : m.id || `${m.sender?.username}-${m.timestamp}-${index}`;

/**
 * One entry in a Kick chat feed: the "New Messages" divider, a moderation notice, a sub/gift
 * notice, a celebration, or a chat message with its reply context and action buttons.
 * Purely presentational: the viewer's permissions arrive as booleans from KickChat.
 *
 * showModeration   the viewer may see moderation notices (moderators/broadcaster)
 * canModerateUser  the viewer may moderate this message's sender (opens the user panel)
 * canDelete        ...and has the delete scope
 */
const KickFeedItem = ({
  message: m,
  currentUserId = null,
  currentUsername = null,
  channelSubBadges,
  showModeration = false,
  canModerateUser = false,
  canDelete = false,
  onUserClick,
  onReply,
  onDelete,
}) => {
  if (m.type === "separator") {
    return (
      <div className="new-messages">
        New Messages
      </div>
    );
  }

  // ⭐ MODERATION: Render moderation system messages (only for mods/broadcasters)
  if (m.type === "moderation") {
    if (!showModeration) {
      return null;
    }

    let icon = '';
    let className = '';

    if (m.moderationType === 'ban') {
      icon = <BannedIcon viewBox="0 0 32 32" style={{ width: 18, height: 18}} />;
      className = 'ban';
    } else if (m.moderationType === 'timeout') {
      icon = <TimeoutIcon viewBox="0 0 32 32" style={{ width: 18, height: 18}} />;
      className = 'timeout';
    } else if (m.moderationType === 'unban') {
      icon = <UnBannedIcon viewBox="0 0 32 32" style={{ width: 18, height: 18}} />;
      className = 'unban';
    }

    return (
      <div className="chat-message moderation-message">
        <div className="icon">{icon}</div>
        <span className={className}>{m.content}</span>
      </div>
    );
  }

  // ⭐ SUBS, GIFTS & CELEBRATIONS: one layout, as on Kick (icon; name, description, the user's message)
  if (EVENT_TYPES.has(m.type)) {
    const { username, description } = describeEvent(m);
    return (
      <div className={`chat-message celebration-message ${m.type}`}>
        <CelebrateIcon className="celebration-icon" viewBox="0 0 32 32" />
        <div className="celebration-body">
          <span className="celebration-text">
            {username && <strong className="celebration-user">{username}</strong>}
            <span className="celebration-description">{description}</span>
          </span>

          {m.type === 'celebration' && m.content && (
            <div className="celebration-user-message">
              {renderBadges(m.sender, channelSubBadges)}
              <strong style={{ color: m.sender?.identity?.color || "#fff" }}>{m.sender?.username}</strong>
              :{" "}
              <span className="celebration-user-message-content">{renderMessageContent(m.content)}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Check for reply data - based on actual Kick.com structure
  const replyData = m.metadata?.original_message
    ? {
        id: m.metadata.original_message.id,
        content: m.metadata.original_message.content,
        sender: {
          id: m.metadata.original_sender?.id,
          username: m.metadata.original_sender?.username || 'Unknown'
        }
      }
    : null;

  // Check if current user is mentioned in the message
  const isMentioned = currentUsername && m.content &&
    m.content.toLowerCase().includes(`@${currentUsername.toLowerCase()}`);

  const senderIdStr = String(m.sender?.id || '');
  const showReply = currentUserId && senderIdStr && senderIdStr !== currentUserId && !m.deleted;

  return (
    <div
      className={`chat-message-wrapper ${isMentioned ? 'mentioned' : ''} ${m.deleted ? 'deleted-message' : ''}`}
      data-message-id={m.id}
      data-user-id={m.sender?.id}
    >
      {/* Show reply context if this message is a reply */}
      {replyData && (
        <div
          className="message-reply-context"
          title={`Reply to ${replyData.sender?.username || replyData.username}: ${replyData.content}`}
        >
          <span className="reply-icon">
            <ReplyIcon style={{ width: 12, height: 12 }} title="Reply" className="reply-icon" />
          </span>
          <span className="replied-to-intro">Replying to</span>
          <span className="replied-to-user">
            {replyData.sender?.username || replyData.username || 'Unknown'}
          </span>
          <span className="replied-to-text">
            {renderTruncatedContent(replyData.content, 50, REPLY_PREVIEW_EMOTE)}
          </span>
        </div>
      )}

      {/* Main message */}
      <div className="chat-message">
        {renderBadges(m.sender, channelSubBadges)}
        <strong
          style={{ color: m.sender?.identity?.color || "#fff" }}
          className={`moderate-user ${canModerateUser ? 'can-moderate' : ''}`}
          onClick={() => canModerateUser && onUserClick?.(m)}
          title={canModerateUser ? 'Click to moderate user' : ''}
        >
          {m.sender?.username || "Unknown"}
        </strong>
        :{" "}
        <span>{renderMessageContent(m.content)}</span>

        {/* Action buttons container */}
        <span className="message-actions">
          {/* Delete button - moderators with the delete scope, for messages they could moderate */}
          {canDelete && m.id && !m.deleted && (
            <button
              className="message-mod-btn message-delete-btn"
              onClick={() => onDelete?.(m)}
              title="Delete this message"
              aria-label={`Delete message from ${m.sender?.username}`}
            >
              <TrashIcon viewBox="0 0 20 20" style={{ width: 14, height: 14 }} />
            </button>
          )}
          {/* Reply button - don't show for own messages or deleted messages */}
          {showReply ? (
            <button
              className="message-reply-btn"
              onClick={() => onReply?.(m)}
              title="Reply to this message"
              aria-label={`Reply to ${m.sender?.username}`}
            >
              <ReplyIcon style={{ width: 12, height: 12 }} title="Reply" className="reply-icon" />
            </button>
          ) : null}
        </span>
      </div>
    </div>
  );
};

export default KickFeedItem;
